import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { cn } from '@/utils/cn';
import { parseDateValue } from '@/utils/dateInput';
import { intlMonthYearLabel } from '@/utils/intlCalendar';
import { CalendarWeekdayHeader } from '@/components/ui/CalendarWeekdayHeader';

export type CalendarPopoverProps = {
  value?: string | null;
  viewMonth: Date;
  onViewMonthChange: (month: Date) => void;
  onSelect: (date: Date) => void;
  min?: string;
  max?: string;
  className?: string;
  /** When true, renders without outer border/shadow for embedding in combined panels. */
  embedded?: boolean;
};

export function CalendarPopover({
  value,
  viewMonth,
  onViewMonthChange,
  onSelect,
  min,
  max,
  className,
  embedded = false,
}: CalendarPopoverProps) {
  const { t } = useTranslation('common');
  const selected = parseDateValue(value);
  const minDate = parseDateValue(min);
  const maxDate = parseDateValue(max);

  const days = useMemo(() => {
    const monthStart = startOfMonth(viewMonth);
    const monthEnd = endOfMonth(viewMonth);
    return eachDayOfInterval({
      start: startOfWeek(monthStart),
      end: endOfWeek(monthEnd),
    });
  }, [viewMonth]);

  const isDisabled = (day: Date) => {
    if (minDate && day < minDate && !isSameDay(day, minDate)) return true;
    if (maxDate && day > maxDate && !isSameDay(day, maxDate)) return true;
    return false;
  };

  return (
    <div
      className={cn(
        embedded
          ? 'px-3 pt-3'
          : 'rounded-xl border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-600 dark:bg-slate-900',
        className,
      )}
      role={embedded ? undefined : 'dialog'}
      aria-label={embedded ? undefined : t('labels.calendar')}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <button
          type="button"
          className="calendar-nav-btn"
          onClick={() => onViewMonthChange(addMonths(viewMonth, -1))}
          aria-label={t('labels.previousMonth')}
        >
          <ChevronLeftIcon sx={{ fontSize: 20 }} />
        </button>
        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {intlMonthYearLabel(viewMonth)}
        </span>
        <button
          type="button"
          className="calendar-nav-btn"
          onClick={() => onViewMonthChange(addMonths(viewMonth, 1))}
          aria-label={t('labels.nextMonth')}
        >
          <ChevronRightIcon sx={{ fontSize: 20 }} />
        </button>
      </div>

      <CalendarWeekdayHeader />

      <div className="grid grid-cols-7 gap-0.5">
        {days.map((day) => {
          const inMonth = isSameMonth(day, viewMonth);
          const selectedDay = selected ? isSameDay(day, selected) : false;
          const disabled = isDisabled(day);
          return (
            <button
              key={day.toISOString()}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(day)}
              className={cn(
                'calendar-day',
                !inMonth && 'calendar-day--muted',
                selectedDay && 'calendar-day--selected',
                isToday(day) && !selectedDay && 'calendar-day--today',
                disabled && 'calendar-day--disabled',
              )}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>
    </div>
  );
}
